/**
 * The lookup-column filter engine for this repo's lists (fleet filter contract v3 §16.8 / C86, adopted 2026-11-03). The UI model is
 * the shared `LookupSelection` (picklistSelection.ts, copied from ptc-core), held directly as each column's filter state; this file
 * applies it to rows and tallies the facet counts the shared `SearchableMultiSelect` shows.
 *
 * A row's value for a column is a LIST of codes, because some columns hold several (a client's taxes, a task's obligation types); a
 * single-valued column passes `[code]`, and an empty list is "has no value". The set semantics are supervision's and core's
 * (`setPredicate`), widened from one value to a list:
 *
 *   in,    no token:  row has a value in codes
 *   in,    token:     row has no value, or has a value in codes
 *   notIn, no token:  row has no value in codes          (rows with no value are kept)
 *   notIn, token:     row has a value, and none in codes (rows with no value are excluded)
 *
 * An empty selection (no codes, no "(has no value)") is no filter, whichever way "Exclude these" is set.
 */
import { NO_VALUE_TOKEN } from '../columnFilterOps';
import { EMPTY_SELECTION, readLookupSelection, writeLookupSelection, type LookupSelection } from './picklistSelection';

export { EMPTY_SELECTION, NO_VALUE_TOKEN };
export type { LookupSelection };

export function isLookupActive(sel: LookupSelection): boolean {
  return sel.codes.length > 0 || sel.includeNoValue;
}

export function matchesLookup(sel: LookupSelection, values: readonly string[]): boolean {
  if (!isLookupActive(sel)) return true;
  if (values.length === 0) return sel.excluding ? !sel.includeNoValue : sel.includeNoValue;
  const hit = values.some((v) => sel.codes.includes(v));
  return sel.excluding ? !hit : hit;
}

/** One lookup column of a list: its applied selection and how to read a row's codes. */
export interface LookupColumn<T> {
  key: string;
  selection: LookupSelection;
  values: (row: T) => readonly string[];
}

/** The rows passing every column's lookup filter, except `skipKey`'s own (its facet base). */
export function applyLookups<T>(rows: readonly T[], columns: readonly LookupColumn<T>[], skipKey?: string): T[] {
  const active = columns.filter((c) => c.key !== skipKey && isLookupActive(c.selection));
  if (active.length === 0) return [...rows];
  return rows.filter((r) => active.every((c) => matchesLookup(c.selection, c.values(r))));
}

/**
 * Facets (§16.3): for column `key`, the rows matching EVERY active filter EXCEPT its own, tallied per code, plus
 * {@link NO_VALUE_TOKEN} for rows with no value. `rows` must already carry the page's other filters (search, presets). Every known
 * option is seeded at 0, so an option with no rows reads "(0)", never a missing count (the defect supervision's gates found).
 * A row with several codes counts once under each distinct one.
 */
export function facetCountsFor<T>(
  rows: readonly T[],
  columns: readonly LookupColumn<T>[],
  key: string,
  optionValues: readonly string[],
): Record<string, number> {
  const column = columns.find((c) => c.key === key);
  const counts: Record<string, number> = { [NO_VALUE_TOKEN]: 0 };
  for (const v of optionValues) counts[v] = 0;
  if (!column) return counts;
  for (const row of applyLookups(rows, columns, key)) {
    const values = Array.from(new Set(column.values(row)));
    if (values.length === 0) counts[NO_VALUE_TOKEN] += 1;
    for (const v of values) counts[v] = (counts[v] ?? 0) + 1;
  }
  return counts;
}

/** A one-option "include" selection (a preset's assignee or owner). */
export function singleSelection(code: string): LookupSelection {
  return { excluding: false, codes: [code], includeNoValue: false };
}

/**
 * The selection as ONE plain code when it is exactly "include this one" (what a preset sets); '' when it is no filter; null when it
 * is anything else, which no preset matches.
 */
export function singleCode(sel: LookupSelection): string | null {
  if (!isLookupActive(sel)) return '';
  return !sel.excluding && !sel.includeNoValue && sel.codes.length === 1 ? sel.codes[0] : null;
}

/** The stored shape of a selection in saved preferences: the fleet's `{op, value}` (picklistSelection), never a private one. */
export interface StoredLookup {
  op: string;
  value: string;
}

export function toStored(sel: LookupSelection): StoredLookup | undefined {
  return isLookupActive(sel) ? writeLookupSelection(sel) : undefined;
}

export function fromStored(stored: StoredLookup | null | undefined): LookupSelection {
  if (!stored || typeof stored.op !== 'string' || typeof stored.value !== 'string') return EMPTY_SELECTION;
  return readLookupSelection(stored.op, stored.value);
}

/**
 * The header chip for an applied selection: up to two names, then "+N"; "Not: …" when excluding. A filled funnel alone didn't read
 * as "this list is filtered" (the Tasks page learned that on Status and Assignee), so every lookup column names its filter.
 */
export function lookupSummary(
  sel: LookupSelection,
  options: readonly { value: string; label: string }[],
  noValueLabel: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!isLookupActive(sel)) return '';
  const names = [
    ...sel.codes.map((c) => options.find((o) => o.value === c)?.label ?? c),
    ...(sel.includeNoValue ? [noValueLabel] : []),
  ];
  const shown = names.slice(0, 2).join(', ');
  const list = names.length > 2 ? t('common.lookupFilter.summaryMore', { list: shown, count: names.length - 2 }) : shown;
  return sel.excluding ? t('common.lookupFilter.summaryExcluding', { list }) : list;
}
